import {
  type ChangeEventHandler,
  type FocusEventHandler,
  forwardRef,
  type KeyboardEventHandler,
  type ReactNode,
} from "react";

import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type TextareaInputProps = {
  background?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  isOptional?: boolean;
  compact?: boolean;
  label?: string | null;
  labelTag?: ReactNode;
  name: string;
  onBlur?: FocusEventHandler;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyUp?: KeyboardEventHandler;
  placeholder?: string;
  resize?: boolean;
  rows?: number;
  value?: number | string;
  defaultValue?: number | string;
  withBorder?: boolean;
  dataTestId?: string;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};

export const TextareaInput = forwardRef<
  HTMLTextAreaElement,
  TextareaInputProps
>(
  (
    {
      background,
      disabled,
      error,
      hint,
      isOptional,
      label,
      labelTag,
      name,
      compact,
      onBlur,
      onChange,
      onKeyDown,
      onKeyUp,
      placeholder,
      resize = true,
      value,
      defaultValue,
      rows = 4,
      withBorder,
      dataTestId,
      fullWidth,
      constrainInputWidth = false,
      ...textAreaProps
    },
    ref,
  ) => {
    const isControlled = value !== undefined;

    return (
      <div className={cnTwMerge(fullWidth && "w-full")}>
        {isTruthy(label) || isTruthy(isOptional) ? (
          <div className={cnTwMerge(fullWidth && "w-full")}>
            <FormLabel
              compact={compact}
              forName={name}
              hint={hint}
              isOptional={isOptional}
              label={label}
              tag={labelTag}
            />
          </div>
        ) : null}
        <div
          className={cnTwMerge(
            "relative mt-1.5 rounded-companyBorderRadius",
            constrainInputWidth && "max-w-[50%]",
          )}
        >
          <textarea
            className={cnTwMerge(
              "block w-full text-text/primary rounded-companyBorderRadius border border-transparent px-2 py-3.5 text-sm placeholder-text/disabled transition focus:border-brand-default focus:ring-0 focus:ring-offset-0 hover:not-disabled:border-brand-default",
              {
                "!border-text/negative": isTruthy(error),
                "bg-dark-gray": isFalsy(background),
                "border-border/primary": isTruthy(withBorder),
                "border-transparent": isFalsy(withBorder),
                "resize-none": !resize,
                resize: resize,
                "py-1 text-sm": compact,
                "cursor-not-allowed bg-background/component/10": disabled,
              },
              background,
            )}
            data-testid={dataTestId ?? `${name}-textarea-input`}
            disabled={disabled}
            id={name}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            placeholder={placeholder}
            ref={ref}
            rows={rows}
            style={{ resize: "vertical" }}
            {...(isControlled ? { value } : { defaultValue })}
            {...textAreaProps}
          />
        </div>
        {isTruthy(error) ? (
          <ValidationErrorText dataTestId={label ?? ""} withMargin>
            {error}
          </ValidationErrorText>
        ) : null}
      </div>
    );
  },
);

TextareaInput.displayName = "TextareaInput";
